import { useCallback, useState } from "react";
import { toast } from "sonner";
import { createSignup } from "../lib/api";
import { communityLogin } from "../lib/communityApi";
import { extractApiErrorMessage } from "../lib/format";

// Shared signup-form state machine used by both the hero form and the final
// CTA form. Keeps each component small and free of duplicated logic.
//
// After a successful /api/signups POST we also call /api/community/login
// silently to issue a community session token, so the visitor can click
// "Go to Community" and land directly in the feed (no second email entry).
const COMMUNITY_TOKEN_KEY = "na_sg_community_token";

const writeCommunityToken = (token) => {
  try {
    if (token) {
      sessionStorage.setItem(COMMUNITY_TOKEN_KEY, token);
    }
  } catch (err) {
    console.error("sessionStorage write failed:", err);
  }
};

export function useSignupForm({ source, successMessage }) {
  const [form, setForm] = useState({ firstName: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const reset = useCallback(() => {
    setForm({ firstName: "", email: "" });
  }, []);

  const clearSuccess = useCallback(() => {
    setLastResult(null);
  }, []);

  const submit = useCallback(
    async (onSuccess) => {
      if (!form.firstName.trim() || !form.email.trim()) {
        toast.error("Please share your first name and email.");
        return false;
      }
      setSubmitting(true);
      try {
        const res = await createSignup({
          firstName: form.firstName.trim(),
          email: form.email.trim(),
          source,
        });
        if (res?.already_member) {
          toast.success("You're already in. Welcome back.");
        } else {
          toast.success(successMessage || "Welcome. We're glad you're here.");
        }

        // Best-effort silent community sign-in so the user doesn't need to
        // re-enter their email on /community.
        try {
          const session = await communityLogin(form.email.trim());
          if (session?.token) {
            writeCommunityToken(session.token);
          }
        } catch (autoLoginErr) {
          console.error("Auto community login failed:", autoLoginErr);
        }

        setLastResult({
          firstName: form.firstName.trim(),
          email: form.email.trim(),
          alreadyMember: Boolean(res?.already_member),
        });
        reset();
        if (typeof onSuccess === "function") {
          onSuccess(res);
        }
        return true;
      } catch (err) {
        console.error("Signup submission failed:", err);
        toast.error(extractApiErrorMessage(err, "Please check your details."));
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [form, source, successMessage, reset]
  );

  return {
    form,
    submitting,
    handleChange,
    submit,
    reset,
    lastResult,
    clearSuccess,
  };
}
